import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import AnchorLink from 'react-anchor-link-smooth-scroll';
import { useTheme } from 'styled-components';

import { InternalStyledLink, ExternalStyledLink } from './styles';

function Link({
  id,
  className,
  children,
  to,
  ariaLabel,
  queryString,
  external,
  noSpan,
  onClick,
  lang,
  target
}) {
  const localePrefix =
  lang === 'en-in' || lang === 'in-en' ? '/in-en' : '/main-en';
  const isJumpLink = /^#/.test(to);
  const isInternal = external ? false : /^\/(?!\/)/.test(to);
  const isProdigy = /^https?:\/\/(www.)?prodigygame\.(com|org|net)/.test(to);
  const isSpecialLink = /\btel:\b|\bmailto:\b/.test(to);
  const child = noSpan ? children : <span>{children}</span>;

  const trackClick = () => {
    if (window.analytics) {
      window.analytics.track('Link Clicked', {
        destination: to,
        link_text: children.toString(),
        external,
        is_special_link: isSpecialLink,
      });
    }
  };

  /* Function to validate all the link cases */
  const linkValidation = (link) => {
    /* don't validate null links and remove empty spaces at end of string */
    if (link === null) return null;
    let validLink = `${
      link[link.length - 1] === '' ? link.slice(0, -1) : link
    }`;

    /* removing double slashes */
    if (
      validLink[validLink.length - 1] === '/' &&
      validLink[validLink.length - 2] === '/'
    ) {
      try {
        validLink = validLink.slice(0, -1);
      } catch (e) {
        console.warn(e);
      }
    }

    /* Break down the link into parts and use path to evulate wether to add trailing slash */
    const parts = validLink.split('/');
    const path =
      parts[parts.length - 1] === ''
        ? parts[parts.length - 2]
        : parts[parts.length - 1];
    let linkReturn = validLink;

    /* check if url contains any doc types (imgs,pdfs,excel), query params, or anchor */
    if (path.includes('.') || path.includes('?') || path.includes('#')) {
      /* check for any trailing slashes and remove them if found, return url w/ trailing slash */
      if (linkReturn[linkReturn.length - 1] === '/') {
        linkReturn = linkReturn.slice(0, -1);
      }
    } else if (linkReturn[linkReturn.length - 1] !== '/') {
      /* otherwise add the trailing slash if one doesn't exist already */
      linkReturn += '/';
    }

    return linkReturn;
  };

  const linkTo = linkValidation(to);

  const setParameters = (string) => {
    if (queryParameters.length > 0) {
      const queryParameter = queryParameters.join('&');
      return `${string}/?${queryParameter}`;
    }
    return string;
  };
  const [queryParameters, setQueryParameters] = useState([]);

  // adjust padding on CTAs for prodigy sans (see button styles)
  const themeFont = useTheme().fonts.primary;
  const isProdigyFont = themeFont.includes('Prodigy');

  // * When the page first mounts we check for a filterQuery in the url
  useEffect(() => {
    if (typeof document !== 'undefined' && queryString) {
      const params = new URL(document.location).searchParams;
      const queries = queryString.split(',');
      queries.forEach((query) => {
        const key = query.trim();
        const queryParam = params.get(key);
        if (queryParam) {
          setQueryParameters((prevState) => {
            const newState = [...prevState, `${key}=${queryParam}`];
            return newState;
          });
        }
      });
    }
  }, []);

  if (isJumpLink) {
    return (
      <AnchorLink
        id={id}
        className={[className, isProdigyFont ? 'ProdigySans' : '']}
        href={linkTo}
        aria-label={ariaLabel}
      >
        {child}
      </AnchorLink>
    );
  }
  if (isInternal) {
    return (
      <InternalStyledLink
        id={id}
        className={[className, isProdigyFont ? 'ProdigySans' : '']}
        to={`${localePrefix}${setParameters(linkTo)}`}
        aria-label={ariaLabel}
        onClick={onClick}
      >
        {child}
      </InternalStyledLink>
    );
  }
  if (isProdigy) {
    return (
      <ExternalStyledLink
        id={id}
        className={[className, isProdigyFont ? 'ProdigySans' : '']}
        href={linkTo}
        aria-label={ariaLabel}
        isSpecialLink
        onClick={() => trackClick()}
      >
        {child}
      </ExternalStyledLink>
    );
  }
  return (
    <ExternalStyledLink
      id={id}
      className={[className, isProdigyFont ? 'ProdigySans' : '']}
      href={external ? `${localePrefix}${setParameters(linkTo)}` : linkTo}
      aria-label={ariaLabel}
      isSpecialLink={isSpecialLink}
      onClick={() => trackClick()}
      target={target}
    >
      {child}
    </ExternalStyledLink>
  );
}

Link.propTypes = {
  id: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  to: PropTypes.string,
  ariaLabel: PropTypes.string,
  external: PropTypes.bool,
  noSpan: PropTypes.bool,
  queryString: PropTypes.string,
  onClick: PropTypes.func,
  lang: PropTypes.string,
  target: PropTypes.string,
};
Link.defaultProps = {
  id: null,
  className: null,
  to: '/',
  ariaLabel: null,
  external: false,
  noSpan: false,
  queryString: null,
  onClick: null,
  children: null,
  lang: 'en-ca',
  target: null,
};
export default Link;
